import { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { Center, CircularProgress, Flex, Spacer } from '@chakra-ui/react'
import { useAppDispatch } from '../../app/typedReduxHooks'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AuthenticationClient,
  AuthenticationResponse,
} from '../../features/authentication/slice/authenticationSlice'
import { routes } from '../routes'
import { appDidFinishLaunching } from '../../app/appSlice'
import { ssoStorage, resolveClientFromOAuthState } from '../../features/sso'
import { usePostHog } from 'posthog-js/react'

const AuthenticationPage: FunctionComponent<unknown> = () => {
  const effectRan = useRef(false)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const posthog = usePostHog()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const navigate = useNavigate()

  const authenticateUser = useCallback(async () => {
    try {
      const code = new URLSearchParams(location.search).get('code')
      const error = new URLSearchParams(location.search).get('error')
      const errorDescription = new URLSearchParams(location.search).get('error_description')
      const state = new URLSearchParams(location.search).get('state')
      const isPopupAuth = state === 'popup_auth=true'
      const clientId =
        location?.pathname?.split('/auth/')?.[1] || process.env.REACT_APP_COGNITO_CLIENT_ID
      const isPopup = window.opener && window.opener !== window && isPopupAuth
      if (clientId && !isPopupAuth) {
        // Prefer the OAuth-state-validated config; otherwise keep whatever was
        // already stored at SSO landing (resolvedConfig from the BFF registry).
        const ssoClient =
          state !== null && state !== '' ? resolveClientFromOAuthState(state) : undefined
        if (ssoClient) {
          ssoStorage.mergeContext({
            clientName: ssoClient.clientName,
            providerId: ssoClient.providerId,
            providerPlatform: ssoClient.providerPlatform,
            providerType: ssoClient.providerType,
            resolvedConfig: ssoClient,
          })
        }
      }

      if (error || errorDescription) {
        if (error) {
          localStorage.setItem('sso_error', error)
        }
        if (errorDescription) {
          localStorage.setItem('sso_error_description', errorDescription)
        }

        if (isPopup) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error, errorDescription }, '*')
          window.close()
        } else {
          return navigate(routes.login.path)
        }
        return
      }

      if (!code || !clientId) {
        if (isPopup) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'No code or clientId' }, '*')
          window.close()
        } else {
          return navigate(routes.login.path)
        }
        return
      }
      if (isPopup) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code,
              redirectUri: `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`,
              clientId: clientId || '',
            }),
          })

          if (!response.ok) {
            throw new Error('Authentication failed')
          }

          const data = await response.json()
          const { userId } = data || {}

          const expectedUserSub = localStorage.getItem('expected_user_sub')
          if (expectedUserSub && userId !== expectedUserSub) {
            window.opener.postMessage(
              {
                type: 'SSO_AUTH_ERROR',
                error: 'User mismatch - please login with the same account',
              },
              '*'
            )
            window.close()
            return
          }
          window.opener.postMessage({ type: 'SSO_AUTH_SUCCESS' }, '*')
          window.close()
          return
        } catch (error) {
          window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'Authentication error' }, '*')
          window.close()
          return
        }
      }
      localStorage.removeItem('_isExpired')
      const { payload } = await dispatch(
        AuthenticationClient.Authenticate({
          code,
          redirectUri: `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`,
          clientId: clientId || '',
        })
      )
      const { assessToken, refreshToken, idToken, userId, firstName, familyName, email } =
        (payload as { data?: AuthenticationResponse }).data || {}
      if (userId && posthog) {
        posthog.identify(userId, {
          firstName,
          familyName,
          email,
        })
      }
      if (assessToken && refreshToken && clientId && idToken) {
        localStorage.setItem('access_token', assessToken)
        localStorage.setItem('refresh_token', refreshToken)
        localStorage.setItem('client_id', clientId)
        localStorage.setItem('id_token', idToken)

        timeoutRef.current = setTimeout(() => {
          navigate(routes.login.path)
        }, 30000)

        setTimeout(async () => {
          try {
            await dispatch(appDidFinishLaunching()).unwrap()
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }
            const redirectUrl = ssoStorage.getAndClearRedirectUrl()
            if (redirectUrl) {
              navigate(redirectUrl)
            } else {
              navigate(routes.root.path)
            }
          } catch (error) {
            navigate(routes.login.path)
          }
        }, 1000)
      } else {
        navigate(routes.login.path)
      }
    } catch (error) {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: 'SSO_AUTH_ERROR', error: 'Authentication error' }, '*')
        window.close()
      } else {
        navigate(routes.login.path)
      }
    }
  }, [dispatch, navigate, location.search])

  useEffect(() => {
    if (!effectRan.current) {
      authenticateUser()
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      effectRan.current = true
    }
  }, [authenticateUser])

  return (
    <Flex flexDirection="column" minWidth="max-content" alignItems="center" gap="10">
      <Spacer />
      <Center w="100px">
        <CircularProgress isIndeterminate color="blue.300" />
      </Center>
      <Center w="intrinsic">We are logging you in...</Center>
    </Flex>
  )
}

export default AuthenticationPage
